import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Settings,
  Tag,
  FileText,
  BarChart3,
  Mail,
  DollarSign,
  PackageCheck,
  MessageSquare,
  MessageCircle,
  Phone,
  Globe,
  AlertTriangle,
  LayoutGrid,
  Loader2,
} from "lucide-react";

import { fetchMerchantConfig } from "../api/merchantConfigClient";

import ConfigCard from "../components/ConfigCard";
import ConfigDetailWrapper from "../components/ConfigDetailWrapper";
import { WelcomeOverlay, SetupWizardBanner, useWelcomeGuide } from "../components/ConfigGuide";

import { GeneralTab } from "../components/config-tabs/general-tab";
import { ShipmentLabelTab } from "../components/config-tabs/shipment-label-tab";
import { ShipmentInvoiceTab } from "../components/config-tabs/shipment-invoice-tab";
import { OrderSummaryTab } from "../components/config-tabs/order-summary-tab";
import { ScheduledEmailTab } from "../components/config-tabs/scheduled-email-tab";
import { CodRemittanceTab } from "../components/config-tabs/cod-remittance-tab";
import { ExternalNotesTab } from "../components/config-tabs/external-notes-tab";
import { SmsNotificationTab } from "../components/config-tabs/sms-notification-tab";
import { WhatsappNotificationTab } from "../components/config-tabs/whatsapp-notification-tab";
import { OrderConfirmationTab } from "../components/config-tabs/order-confirmation-tab";
import { NdrInteractionTab } from "../components/config-tabs/ndr-interaction-tab";
import { WebhookTab } from "../components/config-tabs/webhook-tab";

// ---------------------------------------------------------------------------
// Configuration registry
// ---------------------------------------------------------------------------

const MERCHANT_CONFIG_KEYS = [
  "MERCHANT_GENERAL_CONFIGURATIONS",
  "MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS",
  "MERCHANT_SHIPMENT_INVOICE_CONFIGURATIONS",
  "MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS",
  "MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS",
  "MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS",
  "MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS",
  "MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS",
  "MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS",
  "MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS",
  "MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS",
  "MERCHANT_WEBHOOK_CONFIGURATIONS",
];

const CONFIG_CATEGORIES = [
  { key: MERCHANT_CONFIG_KEYS[0], label: "General Settings", icon: Settings, description: "Company logo, GST details, and feature flags for your account", group: "General" },
  { key: MERCHANT_CONFIG_KEYS[1], label: "Shipment Label", icon: Tag, description: "Customize label templates, layout, and generation strategy", group: "Documents" },
  { key: MERCHANT_CONFIG_KEYS[2], label: "Shipment Invoice", icon: FileText, description: "Invoice templates, numbering, tax details, and disclaimers", group: "Documents" },
  { key: MERCHANT_CONFIG_KEYS[3], label: "Order Summary Report", icon: BarChart3, description: "Report columns, date ranges, and export format settings", group: "Reports" },
  { key: MERCHANT_CONFIG_KEYS[4], label: "Scheduled Email Reports", icon: Mail, description: "Automate status-history reports delivered to your inbox", group: "Reports" },
  { key: MERCHANT_CONFIG_KEYS[5], label: "COD Remittance", icon: DollarSign, description: "Remittance cycle, bank details, and reconciliation settings", group: "Reports" },
  { key: MERCHANT_CONFIG_KEYS[6], label: "Amazon Shipment Notes", icon: PackageCheck, description: "Provider dimensions, weights, and external note mappings", group: "Integration" },
  { key: MERCHANT_CONFIG_KEYS[7], label: "SMS Notifications", icon: MessageSquare, description: "SMS templates, event triggers, and delivery preferences", group: "Notifications" },
  { key: MERCHANT_CONFIG_KEYS[8], label: "WhatsApp Notifications", icon: MessageCircle, description: "WhatsApp message templates and event-based triggers", group: "Notifications" },
  { key: MERCHANT_CONFIG_KEYS[9], label: "Order Confirmation", icon: Phone, description: "Confirmation criteria, IVR flow, and retry settings", group: "Order Flow" },
  { key: MERCHANT_CONFIG_KEYS[10], label: "NDR Customer Interaction", icon: AlertTriangle, description: "NDR communication channels, retries, and escalation rules", group: "Order Flow" },
  { key: MERCHANT_CONFIG_KEYS[11], label: "Webhook", icon: Globe, description: "Outbound event webhooks for real-time integration", group: "Integration" },
];

const TAB_COMPONENTS = {
  MERCHANT_GENERAL_CONFIGURATIONS: GeneralTab,
  MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS: ShipmentLabelTab,
  MERCHANT_SHIPMENT_INVOICE_CONFIGURATIONS: ShipmentInvoiceTab,
  MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS: OrderSummaryTab,
  MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS: ScheduledEmailTab,
  MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS: CodRemittanceTab,
  MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS: ExternalNotesTab,
  MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS: SmsNotificationTab,
  MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS: WhatsappNotificationTab,
  MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS: OrderConfirmationTab,
  MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS: NdrInteractionTab,
  MERCHANT_WEBHOOK_CONFIGURATIONS: WebhookTab,
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function MerchantConfigPage() {
  const reduxMerchantId = useSelector((state) => state?.auth?.merchantId);
  const merchantId = reduxMerchantId || (typeof window !== "undefined" ? localStorage.getItem("merchantId") : null);

  const [activeCategory, setActiveCategory] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [configStatuses, setConfigStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  const { showWelcome, dismissWelcome } = useWelcomeGuide();

  // Load config status for all categories
  const loadAllStatuses = useCallback(async () => {
    if (!merchantId) return;
    setStatusLoading(true);
    const statuses = {};

    const promises = MERCHANT_CONFIG_KEYS.map(async (configKey) => {
      try {
        const data = await fetchMerchantConfig({ merchantId, configKey });
        statuses[configKey] = {
          configured: true,
          metadata: {
            created_date_time: data.created_date_time,
            updated_date_time: data.updated_date_time,
          },
        };
      } catch (e) {
        statuses[configKey] = { configured: false, metadata: null };
      }
    });

    await Promise.allSettled(promises);
    setConfigStatuses(statuses);
    setStatusLoading(false);
  }, [merchantId]);

  useEffect(() => {
    loadAllStatuses();
  }, [loadAllStatuses]);

  const configuredCount = useMemo(
    () => Object.values(configStatuses).filter((s) => s.configured).length,
    [configStatuses]
  );

  const unconfiguredCategories = useMemo(
    () => CONFIG_CATEGORIES.filter((c) => !configStatuses[c.key]?.configured),
    [configStatuses]
  );

  const handleCardClick = useCallback((category) => {
    setActiveCategory(category);
    setReloadKey((k) => k + 1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleBack = useCallback(() => {
    setActiveCategory(null);
    loadAllStatuses();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [loadAllStatuses]);

  const handleWelcomeDismiss = () => {
    dismissWelcome();
  };

  const handleStartWizard = () => {
    dismissWelcome();
    setShowWizard(true);
  };

  // No merchant
  if (!merchantId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-4 sm:p-6">
        <div className="mx-auto max-w-6xl">
          <header className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Account Configuration</h1>
            <p className="mt-1 text-sm text-gray-500">View and manage all configuration categories for your account.</p>
          </header>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            We couldn't determine which merchant account to load. Please sign in again or refresh the page.
          </div>
        </div>
      </div>
    );
  }

  // Detail view (drill-in)
  if (activeCategory) {
    const TabComponent = TAB_COMPONENTS[activeCategory.key];
    return (
      <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
        <div className="w-full min-w-0 max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6">
          <ConfigDetailWrapper category={activeCategory} onBack={handleBack}>
            {TabComponent && <TabComponent merchantId={merchantId} reloadKey={reloadKey} />}
          </ConfigDetailWrapper>
        </div>
      </div>
    );
  }

  // Grid view (dashboard)
  return (
    <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="w-full min-w-0 max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6">
        {/* Welcome overlay for first-time users */}
        {showWelcome && (
          <WelcomeOverlay
            onDismiss={handleWelcomeDismiss}
            onStartWizard={handleStartWizard}
            configuredCount={configuredCount}
            totalCount={MERCHANT_CONFIG_KEYS.length}
          />
        )}

        {/* Page header */}
        <div className="mb-5 sm:mb-6">
          <div className="flex flex-col gap-2">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2 sm:gap-3">
                <LayoutGrid className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-blue-600 shrink-0" />
                Account Configuration
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                Manage all your account settings in one place. Click any card to configure.
              </p>
            </div>
          </div>
        </div>

        {/* Setup wizard banner */}
        {showWizard && unconfiguredCategories.length > 0 && (
          <div className="mb-6">
            <SetupWizardBanner
              unconfiguredCategories={unconfiguredCategories}
              onGoToCategory={handleCardClick}
              onDismiss={() => setShowWizard(false)}
            />
          </div>
        )}

        {/* Loading skeleton */}
        {statusLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl sm:rounded-2xl border border-slate-200/80 p-4 sm:p-5 min-h-[140px] animate-pulse">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-100" />
                  <div className="w-20 h-5 rounded-full bg-slate-100" />
                </div>
                <div className="space-y-2">
                  <div className="w-3/4 h-4 rounded bg-slate-100" />
                  <div className="w-full h-3 rounded bg-slate-50" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Card grid */}
        {!statusLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {CONFIG_CATEGORIES.map((category) => (
              <ConfigCard
                key={category.key}
                category={category}
                status={configStatuses[category.key]?.configured ? "configured" : "not_configured"}
                metadata={configStatuses[category.key]?.metadata}
                onClick={() => handleCardClick(category)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
